import React, { useState } from 'react';
import { Save, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { apiCall, useAdminData, ImageUploader } from './_helpers';

const NavManager = ({ onMessage }) => {
  const [nav, setNav, refetch] = useAdminData('nav', {});
  const [saving, setSaving] = useState(false);

  const links = nav.links || [];
  const updateLink = (idx, patch) => setNav({ ...nav, links: links.map((l, i) => (i === idx ? { ...l, ...patch } : l)) });
  const removeLink = (idx) => setNav({ ...nav, links: links.filter((_, i) => i !== idx) });
  const addLink = () => setNav({ ...nav, links: [...links, { label: '', path: '', order: links.length + 1, is_active: true, is_external: false }] });
  const move = (idx, dir) => {
    const next = [...links];
    const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    next.forEach((l, i) => (l.order = i + 1));
    setNav({ ...nav, links: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiCall('PUT', 'nav', { ...nav, links: (nav.links || []).map((l, i) => ({ ...l, order: i + 1 })) });
      onMessage('success', 'Navigation saved');
      refetch();
    } catch (e) { onMessage('error', e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-md">
        <h2 className="text-2xl font-bold mb-6">Navigation</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Logo</label>
            <ImageUploader currentImage={nav.logo_url} onUpload={(url) => setNav({ ...nav, logo_url: url })} />
            <input type="text" value={nav.logo_url || ''} onChange={(e) => setNav({ ...nav, logo_url: e.target.value })} placeholder="or paste URL" className="w-full border rounded px-3 py-2 text-sm mt-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Logo Alt Text</label>
            <input type="text" value={nav.logo_alt || ''} onChange={(e) => setNav({ ...nav, logo_alt: e.target.value })} className="w-full border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Background Image (navbar)</label>
            <input type="text" value={nav.background_image || ''} onChange={(e) => setNav({ ...nav, background_image: e.target.value })} className="w-full border rounded px-3 py-2" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">CTA Button Label (optional)</label>
              <input type="text" value={nav.cta_label || ''} onChange={(e) => setNav({ ...nav, cta_label: e.target.value })} className="w-full border rounded px-3 py-2" placeholder="Reserve on Resy" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">CTA Button Link</label>
              <input type="text" value={nav.cta_link || ''} onChange={(e) => setNav({ ...nav, cta_link: e.target.value })} className="w-full border rounded px-3 py-2" placeholder="https://resy.com/cities/los-angeles-ca/venues/bricklane" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Menu Links</h3>
          <button onClick={addLink} className="bg-[#C9A86C] text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#B8956A]"><Plus size={16} /> Add Link</button>
        </div>
        <div className="space-y-2">
          {links.map((link, idx) => (
            <div key={idx} className="border rounded-lg p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
              <input type="text" value={link.label} onChange={(e) => updateLink(idx, { label: e.target.value })} placeholder="Label" className="border rounded px-3 py-2 text-sm md:col-span-3" />
              <input type="text" value={link.path} onChange={(e) => updateLink(idx, { path: e.target.value })} placeholder="/path" className="border rounded px-3 py-2 text-sm md:col-span-4" />
              <label className="flex items-center gap-1 text-xs md:col-span-2">
                <input type="checkbox" checked={link.is_active !== false} onChange={(e) => updateLink(idx, { is_active: e.target.checked })} /> Active
              </label>
              <label className="flex items-center gap-1 text-xs md:col-span-2">
                <input type="checkbox" checked={link.is_external || false} onChange={(e) => updateLink(idx, { is_external: e.target.checked })} /> External
              </label>
              <div className="flex gap-1 md:col-span-1 justify-end">
                <button onClick={() => move(idx, -1)} className="p-1 hover:bg-gray-100 rounded"><ArrowUp size={14} /></button>
                <button onClick={() => move(idx, 1)} className="p-1 hover:bg-gray-100 rounded"><ArrowDown size={14} /></button>
                <button onClick={() => removeLink(idx)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} className="bg-[#C9A86C] text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-[#B8956A] disabled:opacity-50">
        <Save size={18} /> {saving ? 'Saving…' : 'Save Navigation'}
      </button>
    </div>
  );
};

export default NavManager;
